import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { api, notifyError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldCheck, Key, SignOut, ArrowRight, Sparkle, CheckCircle, UsersThree } from "@phosphor-icons/react";

const STAGES = ["Pre-seed", "Seed", "Series A+", "Established", "Other"];
const TEAM_SIZES = ["1–5", "6–20", "21–50", "50+"];
const JURISDICTIONS = ["Ireland", "UK", "US"];

// Signed-in but not-yet-granted users land here. Pre-launch is invite-only:
// step 1 = enter code, step 2 = short founding-member questions, then unlock.
// A team invite (?team_invite=<token>) short-circuits to a one-tap accept.
export default function AccessGate() {
  const { user, refresh, logout } = useAuth();
  const [step, setStep] = useState("code");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState({ stage: "", jurisdictions: [], team_size: "", grants: "", pain: "" });
  const [teamToken] = useState(() =>
    new URLSearchParams(window.location.search).get("team_invite") || sessionStorage.getItem("pc_team_invite") || "");
  const [teamInvite, setTeamInvite] = useState(null);

  useEffect(() => {
    if (!teamToken) return;
    sessionStorage.setItem("pc_team_invite", teamToken);
    api.get(`/team/invite-preview/${teamToken}`).then((r) => setTeamInvite(r.data)).catch(() => setTeamInvite(null));
  }, [teamToken]);

  const acceptTeam = async () => {
    setBusy(true);
    try {
      const r = await api.post("/team/accept", { token: teamToken });
      sessionStorage.removeItem("pc_team_invite");
      toast.success(`You've joined ${r.data.company_name}`);
      await refresh();
      window.location.href = "/dashboard";
    } catch (err) { notifyError(err); } finally { setBusy(false); }
  };

  const validate = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/access/validate-code", { code });
      setStep("questions");
    } catch (err) { notifyError(err); } finally { setBusy(false); }
  };

  const toggleJur = (j) => setAnswers((a) => ({
    ...a, jurisdictions: a.jurisdictions.includes(j) ? a.jurisdictions.filter((x) => x !== j) : [...a.jurisdictions, j],
  }));

  const canSubmit = answers.stage && answers.team_size && answers.jurisdictions.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Please answer the highlighted questions"); return; }
    setBusy(true);
    try {
      await api.post("/access/redeem", { code, answers });
      toast.success("Welcome, founding member — access unlocked");
      await refresh();
      window.location.href = "/dashboard";
    } catch (err) { notifyError(err); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen aurora grain flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="glass rounded-2xl p-8 sm:p-10 w-full max-w-lg max-h-[92vh] overflow-auto" data-testid="access-gate">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#FFB020" }}>
            <ShieldCheck size={22} weight="duotone" color="#050B0A" />
          </div>
          <div>
            <div className="font-display text-lg leading-none">PerCen360</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 mt-1">Invite-only · Founding access</div>
          </div>
        </div>

        {teamInvite ? (
          <>
            <div className="eyebrow mb-2 flex items-center gap-2" style={{ color: "#FFB020" }}><UsersThree size={13} weight="fill" /> Team invite</div>
            <h1 className="font-display text-3xl mb-3">Join {teamInvite.company_name}</h1>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Hi {user?.name?.split(" ")[0] || "there"} — you&apos;ve been invited to <strong className="text-white/85">{teamInvite.company_name}</strong> as <span className="text-[#FFB020]">{teamInvite.role_label}</span>. Accept to get access to the company&apos;s workspace.
            </p>
            <button onClick={acceptTeam} disabled={busy} data-testid="team-accept-btn" className="btn-gold w-full justify-center">
              {busy ? "Joining…" : "Accept & join"} <ArrowRight size={16} weight="bold" />
            </button>
          </>
        ) : step === "code" ? (
          <>
            <div className="eyebrow mb-2" style={{ color: "#FFB020" }}>You&apos;re on the list</div>
            <h1 className="font-display text-3xl mb-3">Enter your invite code</h1>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Hi {user?.name?.split(" ")[0] || "there"} — PerCen360 is invite-only ahead of launch. Redeem your
              code to unlock the platform as a <span className="text-[#FFB020]">founding member</span>.
            </p>
            <form onSubmit={validate} className="space-y-4">
              <div>
                <label className="flex items-center gap-2 text-xs text-white/60 mb-1"><Key size={14} /> Invite code</label>
                <input data-testid="gate-code-input" value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="PC-XXXX-XXXX" autoComplete="off" className="mono uppercase tracking-widest" />
              </div>
              <button type="submit" disabled={busy} data-testid="gate-continue-btn" className="btn-gold w-full justify-center">
                {busy ? "Checking…" : "Continue"} <ArrowRight size={16} weight="bold" />
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="eyebrow mb-2 flex items-center gap-2" style={{ color: "#FFB020" }}><Sparkle size={13} weight="fill" /> Founding member</div>
            <h1 className="font-display text-3xl mb-2">A few quick questions</h1>
            <p className="text-white/60 text-sm leading-relaxed mb-5">This tailors PerCen360 to your setup — and secures your founding-member perks.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label>What stage is your company?</label>
                <select data-testid="q-stage" value={answers.stage} onChange={(e) => setAnswers({ ...answers, stage: e.target.value })}>
                  <option value="">Select…</option>
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label>Which jurisdictions do you operate in?</label>
                <div className="flex flex-wrap gap-2 mt-1" data-testid="q-jurisdictions">
                  {JURISDICTIONS.map((j) => {
                    const on = answers.jurisdictions.includes(j);
                    return (
                      <button type="button" key={j} data-testid={`q-jur-${j}`} onClick={() => toggleJur(j)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition ${on ? "border-[#FFB020] text-[#FFB020] bg-[#FFB020]/10" : "border-white/15 text-white/60 hover:border-white/30"}`}>
                        {on && <CheckCircle size={13} weight="fill" className="inline mr-1" />}{j}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label>Team size</label>
                <select data-testid="q-team" value={answers.team_size} onChange={(e) => setAnswers({ ...answers, team_size: e.target.value })}>
                  <option value="">Select…</option>
                  {TEAM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label>Which grants or reliefs matter most? (optional)</label>
                <input data-testid="q-grants" value={answers.grants} onChange={(e) => setAnswers({ ...answers, grants: e.target.value })} placeholder="e.g. Enterprise Ireland, R&D tax credit" />
              </div>
              <div>
                <label>Your biggest compliance pain right now? (optional)</label>
                <textarea rows="2" data-testid="q-pain" value={answers.pain} onChange={(e) => setAnswers({ ...answers, pain: e.target.value })} />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setStep("code")} className="btn-ghost">Back</button>
                <button type="submit" disabled={busy || !canSubmit} data-testid="gate-redeem-btn" className="btn-gold flex-1 justify-center">
                  {busy ? "Unlocking…" : "Unlock founding access"} <ArrowRight size={16} weight="bold" />
                </button>
              </div>
            </form>
          </>
        )}

        <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between text-sm">
          <a href="/#waitlist" className="text-white/60 hover:text-white transition" data-testid="gate-waitlist-link">Don&apos;t have a code? Join the waitlist</a>
          <button onClick={logout} data-testid="gate-logout-btn" className="text-white/50 hover:text-[#E63946] flex items-center gap-1.5"><SignOut size={15} /> Sign out</button>
        </div>
      </motion.div>
    </div>
  );
}
